import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';

const _wg = (name: string) => (...args: any[]) => (window as any)[name](...args);
const scrollToLastMobileCard = _wg('scrollToLastMobileCard');

// ============================================================================
// Types
// ============================================================================

interface CallCardAttachmentsGridProps {
  attachments?: any[];
  title?: string;
  isReadOnly?: boolean;
  onUpload?: (fileInput: HTMLInputElement) => void;
  onRemove?: (attachment: any) => void;
}

// ============================================================================
// Component
// ============================================================================

export function CallCardAttachmentsGrid({
  attachments = [],
  title = 'Attachments',
  isReadOnly = false,
  onUpload,
  onRemove,
}: CallCardAttachmentsGridProps) {
  const { theme } = useTheme();

  const [isMobile, setIsMobile] = useState(false);
  const prevLengthRef = useRef(attachments?.length || 0);
  const containerRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const attachmentCount = useMemo(() => {
    return attachments?.length || 0;
  }, [attachments]);

  // Watch attachments length for scroll-to-last behavior
  useEffect(() => {
    const newLength = attachments?.length || 0;
    if (newLength > prevLengthRef.current) {
      if (containerRef.current) {
        scrollToLastMobileCard(containerRef.current);
      }
    }
    prevLengthRef.current = newLength;
  }, [attachments]);

  // Mobile check (debounced to prevent re-render thrashing)
  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const checkIfMobile = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => setIsMobile(window.innerWidth <= 1024), 150);
    };
    setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', checkIfMobile);
    return () => {
      window.removeEventListener('resize', checkIfMobile);
      if (timer) clearTimeout(timer);
    };
  }, []);

  const triggerFileUpload = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const onFileChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const fileInput = event.target;
    if (!fileInput.files || fileInput.files.length === 0) return;
    onUpload?.(fileInput);
  }, [onUpload]);

  const openAttachment = useCallback((attachment: any) => {
    if (attachment.signedURL) {
      window.open(attachment.signedURL, '_blank');
    } else {
      (window as any).App.showPopupNotification('Attachment URL not available. Try updating the call card.', 'error');
    }
  }, []);

  const removeAttachment = useCallback((attachment: any) => {
    if (isReadOnly) return;
    onRemove?.(attachment);
  }, [isReadOnly, onRemove]);

  const isImageFile = useCallback((attachment: any): boolean => {
    const filename = attachment.filename || '';
    const ext = filename.toLowerCase().split('.').pop();
    return ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].indexOf(ext) !== -1;
  }, []);

  const formatDate = useCallback((dateStr: string): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = date.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    return (month < 10 ? '0' : '') + month + '/' +
      (day < 10 ? '0' : '') + day + '/' +
      year + ' ' +
      hours + ':' +
      (minutes < 10 ? '0' : '') + minutes + ' ' + ampm;
  }, []);

  const getFileIcon = useCallback((attachment: any): string => {
    const filename = attachment.filename || '';
    const ext = filename.toLowerCase().split('.').pop();
    if (['pdf'].indexOf(ext) !== -1) return 'fa-file-pdf';
    if (['doc', 'docx'].indexOf(ext) !== -1) return 'fa-file-word';
    if (['xls', 'xlsx'].indexOf(ext) !== -1) return 'fa-file-excel';
    if (['ppt', 'pptx'].indexOf(ext) !== -1) return 'fa-file-powerpoint';
    if (['zip', 'rar', '7z'].indexOf(ext) !== -1) return 'fa-file-archive';
    if (['txt'].indexOf(ext) !== -1) return 'fa-file-alt';
    if (['mp4', 'avi', 'mov', 'wmv'].indexOf(ext) !== -1) return 'fa-file-video';
    if (['mp3', 'wav', 'ogg'].indexOf(ext) !== -1) return 'fa-file-audio';
    return 'fa-file';
  }, []);

  return (
    <div className={`attachments-grid ${theme}`} ref={containerRef}>
      {/* Header */}
      <div className="header">
        <h1>{title}</h1>
        <button
          className="btn-add"
          onClick={triggerFileUpload}
          disabled={isReadOnly}
          title={isReadOnly ? 'Edit mode required' : 'Add Attachment'}
        >
          <i className="fas fa-plus"></i> ATTACHMENT
        </button>
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={onFileChange}
          multiple
        />
      </div>

      {/* Desktop Table */}
      <div className="table-container">
        <table>
          <thead>
            <tr>
              <th style={{ width: '80px' }}></th>
              <th style={{ width: '25%' }}>Title</th>
              <th style={{ width: '20%' }}>Filename</th>
              <th style={{ width: '25%' }}>Created</th>
              <th style={{ width: '100px' }}></th>
            </tr>
          </thead>
          {attachmentCount === 0 ? (
            <tbody>
              <tr>
                <td colSpan={5} style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
                  No attachments found
                </td>
              </tr>
            </tbody>
          ) : (
            <tbody>
              {attachments.map((attachment: any) => (
                <tr key={'desktop-' + attachment.id}>
                  <td>
                    <div className="preview-container" onClick={() => openAttachment(attachment)}>
                      {isImageFile(attachment) ? (
                        <img src={attachment.signedURL} className="preview-image" alt="Preview" />
                      ) : (
                        <i className={`fas preview-icon ${getFileIcon(attachment)}`}></i>
                      )}
                    </div>
                  </td>
                  <td>
                    <a role="button" className="title-link" onClick={() => openAttachment(attachment)}>
                      {attachment.title || attachment.filename}
                    </a>
                  </td>
                  <td className="cell-filename">{attachment.filename}</td>
                  <td>
                    <div className="created-info">
                      <span className="created-by">{attachment.createdBy}</span>
                      <span className="created-date">{formatDate(attachment.dateCreated)}</span>
                    </div>
                  </td>
                  <td>
                    <button
                      className="btn-delete"
                      onClick={() => removeAttachment(attachment)}
                      disabled={isReadOnly}
                      title={isReadOnly ? 'Edit mode required' : 'Delete'}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          )}
        </table>
      </div>

      {/* Mobile Cards */}
      <div className="mobile-cards">
        {attachmentCount === 0 ? (
          <div className="empty-mobile">No attachments found</div>
        ) : (
          attachments.map((attachment: any) => (
            <div key={'mobile-' + attachment.id} className="card">
              <div className="card-preview" onClick={() => openAttachment(attachment)}>
                {isImageFile(attachment) ? (
                  <img src={attachment.signedURL} className="card-image" alt="Preview" />
                ) : (
                  <i className={`fas card-icon ${getFileIcon(attachment)}`}></i>
                )}
              </div>
              <div className="card-content">
                <div className="card-title">
                  <a role="button" onClick={() => openAttachment(attachment)}>
                    {attachment.title || attachment.filename}
                  </a>
                </div>
                <div className="card-filename">{attachment.filename}</div>
                <div className="card-meta">
                  <span>{attachment.createdBy}</span>
                  <span>{formatDate(attachment.dateCreated)}</span>
                </div>
              </div>
              <div className="card-actions">
                <button
                  className="btn-delete"
                  onClick={() => removeAttachment(attachment)}
                  disabled={isReadOnly}
                >
                  Delete
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

(window as any).CallCardAttachmentsGridReact = CallCardAttachmentsGrid;
